import React, { useMemo, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

export const MESSAGE_API_CODE_TABS = [
  { id: "php", label: "PHP", file: "example.php", language: "php" },
  { id: "node", label: "Node.js", file: "example.mjs", language: "javascript" },
  { id: "python", label: "Python", file: "example.py", language: "python" },
  { id: "curl", label: "cURL", file: "example.sh", language: "bash" },
  { id: "csharp", label: "C#", file: "Example.cs", language: "csharp" },
];

/**
 * Right-column API examples: language tabs + Prism (vscDarkPlus) + copy.
 * @param {Record<string, string>} examples - keys must match MESSAGE_API_CODE_TABS ids (php, node, python, curl, csharp)
 */
const MessageApiExamplesPanel = ({ examples }) => {
  const [activeCodeTab, setActiveCodeTab] = useState("php");
  const [codeCopied, setCodeCopied] = useState(false);

  const activeTabMeta =
    MESSAGE_API_CODE_TABS.find((t) => t.id === activeCodeTab) || MESSAGE_API_CODE_TABS[0];

  const safeExamples = useMemo(() => examples || {}, [examples]);

  const copyActiveSnippet = async () => {
    const text = safeExamples[activeCodeTab];
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="h-100 d-flex flex-column rounded-3 overflow-hidden border"
      style={{
        borderColor: "rgba(38, 166, 154, 0.25)",
        boxShadow: "0 8px 32px rgba(15, 23, 42, 0.08)",
      }}
    >
      <div
        className="p-3 text-white"
        style={{
          borderTopLeftRadius: "5px",
          borderTopRightRadius: "5px",
          background: "#26a69a",
        }}
      >
        API examples
      </div>
      <div className="px-16 pt-12 pb-12">
        <div
          className="d-flex flex-wrap gap-8 p-8 mx-auto"
          style={{
            maxWidth: "100%",
            borderRadius: "14px",
            background: "linear-gradient(145deg, #e8eef3 0%, #dce4ec 100%)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -1px 0 rgba(0,0,0,0.04)",
            border: "1px solid rgba(38, 166, 154, 0.12)",
          }}
        >
          {MESSAGE_API_CODE_TABS.map((tab) => {
            const isActive = activeCodeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className="border-0 fw-semibold flex-grow-1 flex-sm-grow-0"
                style={{
                  minWidth: "fit-content",
                  padding: "11px 18px",
                  borderRadius: "10px",
                  fontSize: "13px",
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                  transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                  background: isActive
                    ? "linear-gradient(145deg, #26a69a 0%, #1c8f84 55%, #157a72 100%)"
                    : "transparent",
                  color: isActive ? "#fff" : "#5c6b7a",
                  boxShadow: isActive
                    ? "0 4px 16px rgba(38, 166, 154, 0.45), 0 1px 2px rgba(0,0,0,0.08)"
                    : "none",
                  transform: isActive ? "translateY(-1px)" : "none",
                }}
                onClick={() => setActiveCodeTab(tab.id)}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "rgba(255,255,255,0.55)";
                    e.currentTarget.style.color = "#26a69a";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = "#5c6b7a";
                  }
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex-grow-1 d-flex flex-column bg-base" style={{ minHeight: 0 }}>
        <div
          className="d-flex align-items-center justify-content-between flex-wrap gap-8 px-16 py-10"
          style={{
            background: "#0f1419",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <span
            className="font-monospace text-xs d-flex align-items-center gap-6"
            style={{ color: "#8b9cb5" }}
          >
            <span
              aria-hidden
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#26a69a",
                display: "inline-block",
              }}
            />
            {activeTabMeta.file}
          </span>
          <button
            type="button"
            className="btn btn-sm px-12 py-4 text-xs border-0"
            style={{
              background: "rgba(255,255,255,0.08)",
              color: codeCopied ? "#4ade80" : "#e2e8f0",
            }}
            onClick={copyActiveSnippet}
          >
            {codeCopied ? "Copied!" : "Copy"}
          </button>
        </div>
        <div className="flex-grow-1" style={{ minHeight: 0, overflow: "hidden" }}>
          <SyntaxHighlighter
            language={activeTabMeta.language}
            style={vscDarkPlus}
            showLineNumbers
            wrapLines
            wrapLongLines
            lineNumberStyle={{
              minWidth: "2.75em",
              paddingRight: "1em",
              color: "#6e7681",
              fontSize: "11px",
              userSelect: "none",
              borderRight: "1px solid rgba(255,255,255,0.06)",
              marginRight: "14px",
            }}
            codeTagProps={{
              style: {
                fontFamily:
                  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
                fontSize: "12.5px",
              },
            }}
            customStyle={{
              margin: 0,
              padding: "16px 12px 20px 8px",
              maxHeight: "min(440px, 58vh)",
              overflow: "auto",
              background: "#1e1e1e",
              borderRadius: 0,
              lineHeight: 1.65,
            }}
          >
            {safeExamples[activeCodeTab] || ""}
          </SyntaxHighlighter>
        </div>
      </div>
    </div>
  );
};

export default MessageApiExamplesPanel;
